import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHero } from '@/components/ui/PageHero'
import { useMarket } from '@/contexts/MarketContext'
import { useNavigation } from '@/contexts/NavigationContext'
import { formatNumber, formatPercentChange } from '@/lib/format'
import { cn } from '@/lib/cn'

type Bias = 'bullish' | 'bearish' | 'neutral'
type Timeframe = '1H' | '4H' | '1D' | '1W'
type NoteKey = 'note1' | 'note2' | 'note3' | 'note4' | 'note5' | 'note6'

interface Signal {
  id: string
  symbol: string
  bias: Bias
  timeframe: Timeframe
  strength: number
  entry: number
  stop: number
  target: number
  noteKey: NoteKey
  updatedAt: string
}

const MOCK_SIGNALS: Signal[] = [
  {
    id: 's1',
    symbol: 'BTCUSDT',
    bias: 'bullish',
    timeframe: '4H',
    strength: 78,
    entry: 66800,
    stop: 65200,
    target: 69500,
    noteKey: 'note1',
    updatedAt: '12m',
  },
  {
    id: 's2',
    symbol: 'ETHUSDT',
    bias: 'bearish',
    timeframe: '1H',
    strength: 64,
    entry: 3440,
    stop: 3510,
    target: 3280,
    noteKey: 'note2',
    updatedAt: '28m',
  },
  {
    id: 's3',
    symbol: 'XAUUSD',
    bias: 'bullish',
    timeframe: '1D',
    strength: 71,
    entry: 2640,
    stop: 2612,
    target: 2695,
    noteKey: 'note3',
    updatedAt: '1h',
  },
  {
    id: 's4',
    symbol: 'EURUSD',
    bias: 'neutral',
    timeframe: '4H',
    strength: 42,
    entry: 1.084,
    stop: 1.078,
    target: 1.092,
    noteKey: 'note4',
    updatedAt: '2h',
  },
  {
    id: 's5',
    symbol: 'SOLUSDT',
    bias: 'bullish',
    timeframe: '1H',
    strength: 69,
    entry: 176.5,
    stop: 171,
    target: 188,
    noteKey: 'note5',
    updatedAt: '45m',
  },
  {
    id: 's6',
    symbol: 'AAPL',
    bias: 'bearish',
    timeframe: '1D',
    strength: 58,
    entry: 230,
    stop: 236,
    target: 218,
    noteKey: 'note6',
    updatedAt: '3h',
  },
]

/**
 * Analysis — full-bleed research desk aligned with terminal chrome.
 */
export function AnalysisPage({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { setSymbol } = useMarket()
  const { setRoute } = useNavigation()
  const [biasFilter, setBiasFilter] = useState<Bias | 'all'>('all')
  const [tfFilter, setTfFilter] = useState<Timeframe | 'all'>('all')
  const [selectedId, setSelectedId] = useState(MOCK_SIGNALS[0].id)

  const signals = useMemo(() => {
    return MOCK_SIGNALS.filter((s) => {
      if (biasFilter !== 'all' && s.bias !== biasFilter) return false
      if (tfFilter !== 'all' && s.timeframe !== tfFilter) return false
      return true
    })
  }, [biasFilter, tfFilter])

  const selected = signals.find((s) => s.id === selectedId) ?? signals[0]

  const overview = useMemo(() => {
    const bull = MOCK_SIGNALS.filter((s) => s.bias === 'bullish').length
    const bear = MOCK_SIGNALS.filter((s) => s.bias === 'bearish').length
    const neu = MOCK_SIGNALS.filter((s) => s.bias === 'neutral').length
    const avgStrength = Math.round(
      MOCK_SIGNALS.reduce((s, x) => s + x.strength, 0) / MOCK_SIGNALS.length,
    )
    return { bull, bear, neu, total: MOCK_SIGNALS.length, avgStrength }
  }, [])

  return (
    <div
      className={cn(
        'terminal-scroll flex h-full min-h-0 flex-col overflow-y-auto',
        className,
      )}
    >
      <PageHero
        tone="info"
        glow="end"
        eyebrow={t('analysis.eyebrow')}
        title={t('sidebar.analysis')}
        subtitle={t('analysis.subtitle')}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:max-w-2xl">
          <OverviewCard
            label={t('analysis.bullish')}
            value={String(overview.bull)}
            tone="up"
            active={biasFilter === 'bullish'}
            onClick={() =>
              setBiasFilter((v) => (v === 'bullish' ? 'all' : 'bullish'))
            }
          />
          <OverviewCard
            label={t('analysis.bearish')}
            value={String(overview.bear)}
            tone="down"
            active={biasFilter === 'bearish'}
            onClick={() =>
              setBiasFilter((v) => (v === 'bearish' ? 'all' : 'bearish'))
            }
          />
          <OverviewCard
            label={t('analysis.neutral')}
            value={String(overview.neu)}
            active={biasFilter === 'neutral'}
            onClick={() =>
              setBiasFilter((v) => (v === 'neutral' ? 'all' : 'neutral'))
            }
          />
          <OverviewCard
            label={t('analysis.strength')}
            value={`${overview.avgStrength}%`}
          />
        </div>
      </PageHero>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
        <div className="min-w-0 space-y-3 border-e border-terminal-border/30 p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2 rounded border border-terminal-border/40 bg-terminal-surface p-2">
            <FilterGroup
              label={t('analysis.bias')}
              value={biasFilter}
              options={[
                { value: 'all', label: t('analysis.all') },
                { value: 'bullish', label: t('analysis.bullish') },
                { value: 'bearish', label: t('analysis.bearish') },
                { value: 'neutral', label: t('analysis.neutral') },
              ]}
              onChange={(v) => setBiasFilter(v as Bias | 'all')}
            />
            <div className="hidden h-5 w-px bg-terminal-border/50 sm:block" />
            <FilterGroup
              label={t('analysis.timeframe')}
              value={tfFilter}
              options={[
                { value: 'all', label: t('analysis.all') },
                { value: '1H', label: '1H' },
                { value: '4H', label: '4H' },
                { value: '1D', label: '1D' },
                { value: '1W', label: '1W' },
              ]}
              onChange={(v) => setTfFilter(v as Timeframe | 'all')}
            />
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {signals.map((s) => {
              const active = selected?.id === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(s.id)
                    setSymbol(s.symbol)
                  }}
                  className={cn(
                    'rounded border p-3 text-start transition-colors',
                    active
                      ? 'border-info/40 bg-info/[0.06] shadow-[inset_2px_0_0_0_var(--color-info)]'
                      : 'border-terminal-border/40 bg-terminal-surface hover:bg-terminal-elevated/40',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-[13px] font-bold">
                          {s.symbol}
                        </span>
                        <BiasBadge bias={s.bias} />
                        <span className="rounded bg-terminal-elevated px-1 py-px font-mono text-[9px] text-terminal-muted">
                          {s.timeframe}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-[11px] text-terminal-muted">
                        {t(`analysis.${s.noteKey}`)}
                      </p>
                    </div>
                    <div className="shrink-0 text-end">
                      <p className="font-mono text-sm font-bold tabular-nums text-info">
                        {s.strength}
                      </p>
                      <p className="text-[9px] text-terminal-muted">
                        {t('analysis.strength')}
                      </p>
                    </div>
                  </div>
                  <LevelBar signal={s} className="mt-2.5" />
                  <div className="mt-2 flex items-center gap-2 text-[10px] text-terminal-muted">
                    <span>
                      {t('analysis.entry')}{' '}
                      <span className="font-mono text-terminal-fg">
                        {formatNumber(s.entry, { maximumFractionDigits: 5 })}
                      </span>
                    </span>
                    <span className="ms-auto">{s.updatedAt}</span>
                  </div>
                </button>
              )
            })}
            {signals.length === 0 ? (
              <div className="col-span-full rounded border border-dashed border-terminal-border/50 px-4 py-12 text-center text-xs text-terminal-muted">
                {t('analysis.empty')}
              </div>
            ) : null}
          </div>
        </div>

        {selected ? (
          <aside className="border-t border-terminal-border/30 bg-terminal-elevated/15 p-3 sm:p-4 lg:border-t-0">
            <div className="rounded border border-terminal-border/40 bg-terminal-surface">
              <div className="relative overflow-hidden border-b border-terminal-border/40 px-3.5 py-3.5">
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      selected.bias === 'bullish'
                        ? 'radial-gradient(ellipse 70% 80% at 100% 0%, color-mix(in oklab, var(--color-bid) 18%, transparent), transparent 55%)'
                        : selected.bias === 'bearish'
                          ? 'radial-gradient(ellipse 70% 80% at 100% 0%, color-mix(in oklab, var(--color-danger) 18%, transparent), transparent 55%)'
                          : 'radial-gradient(ellipse 70% 80% at 100% 0%, color-mix(in oklab, var(--color-info) 16%, transparent), transparent 55%)',
                  }}
                />
                <div className="relative flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-info">
                      {t('analysis.eyebrow')}
                    </p>
                    <h2 className="mt-0.5 font-mono text-lg font-bold">
                      {selected.symbol}
                    </h2>
                  </div>
                  <BiasBadge bias={selected.bias} />
                </div>
                <p className="relative mt-2 text-xs leading-relaxed text-terminal-muted">
                  {t(`analysis.${selected.noteKey}`)}
                </p>
              </div>

              <div className="space-y-3 p-3.5">
                <StrengthBar
                  value={selected.strength}
                  label={t('analysis.strength')}
                />
                <LevelBar signal={selected} tall />

                <dl className="grid grid-cols-2 gap-2 text-[11px]">
                  <Detail
                    label={t('analysis.timeframe')}
                    value={selected.timeframe}
                  />
                  <Detail
                    label={t('analysis.updated')}
                    value={selected.updatedAt}
                  />
                  <Detail
                    label={t('analysis.entry')}
                    value={formatNumber(selected.entry, {
                      maximumFractionDigits: 5,
                    })}
                  />
                  <Detail
                    label={t('analysis.stop')}
                    value={formatNumber(selected.stop, {
                      maximumFractionDigits: 5,
                    })}
                    tone="down"
                  />
                  <Detail
                    label={t('analysis.target')}
                    value={formatNumber(selected.target, {
                      maximumFractionDigits: 5,
                    })}
                    tone="up"
                  />
                  <Detail
                    label={t('analysis.riskReward')}
                    value={`1 : ${formatNumber(rrOf(selected), { maximumFractionDigits: 2 })}`}
                  />
                </dl>

                <RiskReward signal={selected} />

                <button
                  type="button"
                  onClick={() => {
                    setSymbol(selected.symbol)
                    setRoute('trade')
                  }}
                  className="h-10 w-full rounded bg-info text-sm font-semibold text-white hover:bg-info-muted"
                >
                  {t('analysis.openChart')}
                </button>
              </div>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  )
}

function rrOf(signal: Signal): number {
  const risk = Math.abs(signal.entry - signal.stop)
  const reward = Math.abs(signal.target - signal.entry)
  return risk > 0 ? reward / risk : 0
}

function OverviewCard({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string
  value: string
  tone?: 'up' | 'down'
  active?: boolean
  onClick?: () => void
}) {
  const Comp = onClick ? 'button' : 'div'
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'rounded border px-2.5 py-2 text-start',
        active
          ? 'border-info/40 bg-info/10'
          : 'border-info/15 bg-terminal-surface/90',
        onClick && 'hover:border-info/30',
      )}
    >
      <p className="text-[9px] font-semibold uppercase tracking-wider text-terminal-muted">
        {label}
      </p>
      <p
        className={cn(
          'mt-0.5 font-mono text-sm font-bold tabular-nums',
          tone === 'up' && 'text-bid',
          tone === 'down' && 'text-danger',
          !tone && 'text-terminal-fg',
        )}
      >
        {value}
      </p>
    </Comp>
  )
}

function BiasBadge({ bias }: { bias: Bias }) {
  const { t } = useTranslation('common')
  return (
    <span
      className={cn(
        'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase',
        bias === 'bullish' && 'bg-bid-badge text-bid',
        bias === 'bearish' && 'bg-danger-badge text-danger',
        bias === 'neutral' && 'bg-terminal-elevated text-terminal-muted',
      )}
    >
      {t(`analysis.${bias}`)}
    </span>
  )
}

function FilterGroup({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="me-1 text-[10px] font-semibold uppercase text-terminal-muted">
        {label}
      </span>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'h-7 rounded-sm px-2 text-[11px] font-semibold',
            value === o.value
              ? 'bg-info text-white'
              : 'bg-terminal-elevated text-terminal-muted hover:text-terminal-fg',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Detail({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'up' | 'down'
}) {
  return (
    <div className="rounded border border-terminal-border/30 bg-terminal-bg/80 px-2.5 py-2">
      <dt className="text-terminal-muted">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 font-mono font-semibold tabular-nums text-terminal-fg',
          tone === 'up' && 'text-bid',
          tone === 'down' && 'text-danger',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

function LevelBar({
  signal,
  tall,
  className,
}: {
  signal: Signal
  tall?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  const lows = [signal.entry, signal.stop, signal.target]
  const min = Math.min(...lows)
  const max = Math.max(...lows)
  const span = max - min || 1
  const pct = (n: number) => ((n - min) / span) * 100
  const stop = pct(signal.stop)
  const entry = pct(signal.entry)
  const target = pct(signal.target)
  const bandLeft = Math.min(stop, target)
  const bandWidth = Math.abs(target - stop)

  return (
    <div className={className}>
      <div
        className={cn(
          'relative rounded-full bg-terminal-elevated',
          tall ? 'h-2' : 'h-1.5',
        )}
      >
        <div
          className="absolute inset-y-0 rounded-full bg-gradient-to-r from-danger/35 via-info/25 to-bid/45"
          style={{ left: `${bandLeft}%`, width: `${bandWidth}%` }}
        />
        <span
          className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 rounded-full bg-danger"
          style={{ left: `${stop}%` }}
          title={t('analysis.stop')}
        />
        <span
          className="absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded-full bg-info"
          style={{ left: `${entry}%` }}
          title={t('analysis.entry')}
        />
        <span
          className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 rounded-full bg-bid"
          style={{ left: `${target}%` }}
          title={t('analysis.target')}
        />
      </div>
      {tall ? (
        <div className="mt-1.5 flex justify-between text-[10px] text-terminal-muted">
          <span className="text-danger">{t('analysis.stop')}</span>
          <span className="text-info">{t('analysis.entry')}</span>
          <span className="text-bid">{t('analysis.target')}</span>
        </div>
      ) : null}
    </div>
  )
}

function StrengthBar({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded border border-terminal-border/30 bg-terminal-bg px-3 py-2.5">
      <div className="flex items-center justify-between text-[10px]">
        <span className="font-semibold uppercase tracking-wider text-terminal-muted">
          {label}
        </span>
        <span className="font-mono font-bold text-info">{value}%</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-terminal-elevated">
        <div
          className="h-full rounded-full bg-info"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  )
}

function RiskReward({ signal }: { signal: Signal }) {
  const { t } = useTranslation('common')
  const rr = rrOf(signal)
  const move =
    signal.bias === 'bearish'
      ? (signal.entry - signal.target) / signal.entry
      : (signal.target - signal.entry) / signal.entry

  return (
    <div className="rounded border border-info/20 bg-info/[0.04] px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-info">
        {t('analysis.riskReward')}
      </p>
      <p className="mt-1 font-mono text-base font-bold text-terminal-fg">
        1 : {formatNumber(rr, { maximumFractionDigits: 2 })}
      </p>
      <p className="mt-0.5 text-[11px] text-terminal-muted">
        {t('analysis.rrHint', {
          reward: formatPercentChange(move),
        })}
      </p>
    </div>
  )
}
